/**
 * Parent items list + slide-out edit panel (18.8).
 * Sub-list: BOM versions. Create New Version button.
 */

import { useCallback, useRef, useState } from 'react';
import useEntityList from '../../hooks/useEntityList';
import {
  fetchParentItemsList, createParentItem, updateParentItem,
  deleteParentItem, restoreParentItem, fetchBomVersions, createBomVersion,
  deleteBomVersion, importBom, downloadBomTemplate,
} from '../../api/parentItems';
import { SlideOutPanel, Button, LoadingSpinner, EmptyState, Pagination } from '../common';
import PanelDocuments from './PanelDocuments';

export default function ParentItemList({ search, showDeleted }) {
  const { items, loading, totalCount, page, setPage, refresh } = useEntityList(fetchParentItemsList, { search, showDeleted });
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [bomVersions, setBomVersions] = useState([]);
  const [deletingVersionId, setDeletingVersionId] = useState(null);

  // Import BOM modal
  const [importOpen, setImportOpen] = useState(false);
  const [importForm, setImportForm] = useState({
    parent_item_no: '', parent_description: '', version_label: '', version_notes: '',
  });
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importReport, setImportReport] = useState(null);
  const fileInputRef = useRef(null);

  function openCreate() {
    setForm({ parent_item_no: '', description: '', is_generic: false, is_dnv: false, estimated_labor_hours: '', list_price: '' });
    setCreating(true);
    setSelected(null);
    setBomVersions([]);
  }

  async function openEdit(item) {
    setForm({
      parent_item_no: item.parent_item_no || '',
      description: item.description || '',
      is_generic: !!item.is_generic,
      is_dnv: !!item.is_dnv,
      estimated_labor_hours: item.estimated_labor_hours ?? '',
      list_price: item.list_price ?? '',
    });
    setSelected(item);
    setCreating(false);
    loadBomVersions(item.id);
  }

  async function loadBomVersions(parentItemId) {
    try {
      const resp = await fetchBomVersions({ parent_item: parentItemId, page_size: 50 });
      setBomVersions(resp.data || resp.results || resp || []);
    } catch {
      setBomVersions([]);
    }
  }

  function closePanel() { setSelected(null); setCreating(false); setBomVersions([]); }

  async function handleSave() {
    setSaving(true);
    try {
      const payload = {
        ...form,
        estimated_labor_hours: form.estimated_labor_hours ? parseFloat(form.estimated_labor_hours) : 0,
        list_price: form.list_price ? parseFloat(form.list_price) : 0,
      };
      if (creating) await createParentItem(payload);
      else await updateParentItem(selected.id, payload);
      closePanel();
      refresh();
    } catch (err) {
      alert(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!confirm('Delete this parent item?')) return;
    try { await deleteParentItem(selected.id); closePanel(); refresh(); }
    catch (err) { alert(err.response?.data?.error || 'Delete failed'); }
  }

  async function handleRestore() {
    try { await restoreParentItem(selected.id); closePanel(); refresh(); }
    catch (err) { alert(err.response?.data?.error || 'Restore failed'); }
  }

  async function handleCreateVersion() {
    if (!selected) return;
    try {
      await createBomVersion({
        parent_item: selected.id,
        version_label: `Version ${(bomVersions.length + 1)}`,
        is_active: true,
        is_default: bomVersions.length === 0,
      });
      await loadBomVersions(selected.id);
      refresh();
    } catch (err) {
      alert(err.response?.data?.error || JSON.stringify(err.response?.data) || 'Create version failed');
    }
  }

  async function handleSetDefaultVersion(versionId) {
    try {
      await updateParentItem(selected.id, { default_bom_version: versionId });
      setSelected((prev) => ({ ...prev, default_bom_version: versionId }));
      refresh();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to set default version');
    }
  }

  async function handleDeleteBomVersion(versionId) {
    try {
      await deleteBomVersion(versionId);
      setDeletingVersionId(null);
      await loadBomVersions(selected.id);
      refresh();
    } catch (err) {
      alert(err.response?.data?.error || 'Delete failed');
    }
  }

  function openImport() {
    const itemNo = selected ? selected.parent_item_no : (form.parent_item_no || '');
    const itemDesc = selected ? selected.description : (form.description || '');
    setImportForm({
      parent_item_no: itemNo,
      parent_description: itemDesc,
      estimated_labor_hours: selected ? (selected.estimated_labor_hours ?? '') : (form.estimated_labor_hours || ''),
      list_price: selected ? (selected.list_price ?? '') : (form.list_price || ''),
      version_label: '',
      version_notes: '',
    });
    setImportFile(null);
    setImportReport(null);
    setImportOpen(true);
  }

  async function handleImport() {
    if (!importFile) { alert('Please select a file.'); return; }
    if (!importForm.parent_item_no.trim()) { alert('Parent Item Number is required.'); return; }
    setImporting(true);
    setImportReport(null);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      fd.append('parent_item_no', importForm.parent_item_no.trim());
      fd.append('parent_description', importForm.parent_description.trim());
      if (importForm.estimated_labor_hours) fd.append('estimated_labor_hours', importForm.estimated_labor_hours);
      if (importForm.list_price) fd.append('list_price', importForm.list_price);
      fd.append('version_label', importForm.version_label.trim());
      fd.append('version_notes', importForm.version_notes.trim());
      const result = await importBom(fd);
      const report = result.data || result;
      setImportReport(report);
      refresh();
      if (selected) {
        await loadBomVersions(selected.id);
      } else if (!report.errors?.length) {
        closePanel();
      }
    } catch (err) {
      const details = err.response?.data?.details || err.response?.data;
      setImportReport(details || { errors: [err.message || 'Import failed'] });
    } finally {
      setImporting(false);
    }
  }

  async function handleDownloadTemplate() {
    const blob = await downloadBomTemplate();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bom_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  if (loading) return <div className="flex justify-center py-10"><LoadingSpinner /></div>;

  return (
    <div>
      <div className="px-4 py-2 border-b border-gray-100">
        <Button size="sm" onClick={openCreate}>+ New Parent Item</Button>
      </div>

      {items.length === 0 ? (
        <EmptyState title="No parent items" description="Add parent items (pump assemblies) to define your product catalog." />
      ) : (
        <>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
                <th className="px-4 py-2">Item Number</th>
                <th className="px-4 py-2">Description</th>
                <th className="px-4 py-2 text-center">Flags</th>
                <th className="px-4 py-2 text-right">Est. Labor</th>
                <th className="px-4 py-2 text-right">List Price</th>
                <th className="px-4 py-2 text-center">Versions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((item) => (
                <tr
                  key={item.id}
                  onClick={() => openEdit(item)}
                  className={`hover:bg-blue-50 cursor-pointer ${item.deleted_at ? 'opacity-50 line-through' : ''}`}
                >
                  <td className="px-4 py-2 font-mono text-xs font-medium text-foreground">{item.parent_item_no}</td>
                  <td className="px-4 py-2 text-gray-600 truncate max-w-[250px]">{item.description || '—'}</td>
                  <td className="px-4 py-2 text-center">
                    <div className="flex items-center justify-center gap-1 flex-wrap">
                      {item.is_generic && <span className="text-purple-600 text-xs font-medium">Generic</span>}
                      {item.is_dnv && <span className="text-blue-700 text-xs font-medium">🏷 DNV</span>}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-right text-gray-500">
                    {item.estimated_labor_hours != null ? `${item.estimated_labor_hours}h` : '—'}
                  </td>
                  <td className="px-4 py-2 text-right text-gray-500">
                    {item.list_price != null ? `${item.list_price}` : '—'}
                  </td>
                  <td className="px-4 py-2 text-center text-gray-500">{item.bom_versions_count ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-2">
            <Pagination current={page} total={Math.ceil(totalCount / 200)} onChange={setPage} />
          </div>
        </>
      )}

      <SlideOutPanel
        open={!!selected || creating}
        onClose={closePanel}
        title={creating ? 'New Parent Item' : `Edit: ${selected?.parent_item_no || ''}`}
        width="max-w-lg"
      >
        <div className="space-y-4">
          <TextInput label="Item Number" value={form.parent_item_no} onChange={(v) => setForm({ ...form, parent_item_no: v })} />
          <TextInput label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} multiline />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Labor Hours</label>
              <input
                type="number"
                step="0.5"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                value={form.estimated_labor_hours ?? ''}
                onChange={(e) => setForm({ ...form, estimated_labor_hours: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">List Price ($)</label>
              <input
                type="number"
                step="0.01"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                value={form.list_price ?? ''}
                onChange={(e) => setForm({ ...form, list_price: e.target.value })}
              />
            </div>
          </div>

          {/* Flags */}
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_generic"
                checked={form.is_generic || false}
                onChange={(e) => setForm({ ...form, is_generic: e.target.checked })}
                className="rounded border-gray-300 text-blue-600"
              />
              <label htmlFor="is_generic" className="text-sm text-gray-700 cursor-pointer">Generic (for repairs)</label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_dnv"
                checked={form.is_dnv || false}
                onChange={(e) => setForm({ ...form, is_dnv: e.target.checked })}
                className="rounded border-gray-300 text-blue-600"
              />
              <label htmlFor="is_dnv" className="text-sm text-gray-700 cursor-pointer">🏷 Requires DNV 2.71</label>
            </div>
          </div>

          {/* BOM versions sub-list */}
          <div className="border-t border-gray-200 pt-3">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-semibold text-gray-500 uppercase">
                {selected ? `BOM Versions (${bomVersions.length})` : 'BOM'}
              </h4>
              <Button size="sm" variant="secondary" onClick={openImport}>⬆ Import BOM</Button>
            </div>
            {creating && (
              <p className="text-xs text-gray-400 mb-2">
                Save the parent item first, or import a BOM file to create the item and BOM in one step.
              </p>
            )}
            {selected && bomVersions.length === 0 && (
              <div className="text-xs text-gray-400 bg-yellow-50 border border-yellow-200 rounded px-2 py-1.5">
                No BOM versions yet. Click <strong>⬆ Import BOM</strong> above to import a BOM file.
              </div>
            )}
            {selected && bomVersions.length > 0 && (
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {bomVersions.map((bv) => {
                  const isDefault = selected.default_bom_version === bv.id;
                  return (
                    <div key={bv.id} className="flex items-center justify-between text-xs bg-gray-50 rounded px-2 py-1.5 group">
                      <div className="flex items-center gap-2 min-w-0">
                        <button
                          onClick={() => handleSetDefaultVersion(bv.id)}
                          title={isDefault ? 'Default version' : 'Set as default'}
                          className={`flex-shrink-0 w-3.5 h-3.5 rounded-full border-2 transition-colors ${
                            isDefault
                              ? 'bg-blue-500 border-blue-500'
                              : 'bg-white border-gray-300 hover:border-blue-400'
                          }`}
                        />
                        <span className="font-medium text-foreground">v{bv.version_number}</span>
                        {bv.version_label && <span className="text-gray-500">({bv.version_label})</span>}
                        {isDefault && <span className="text-blue-600 font-medium">Default</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400">{bv.lines_count ?? '—'} lines</span>
                        {deletingVersionId === bv.id ? (
                          <span className="flex items-center gap-1">
                            <span className="text-red-600 font-medium">Delete?</span>
                            <button onClick={() => handleDeleteBomVersion(bv.id)} className="text-red-600 hover:text-red-800 font-medium">Yes</button>
                            <button onClick={() => setDeletingVersionId(null)} className="text-gray-400 hover:text-gray-600">No</button>
                          </span>
                        ) : (
                          <button
                            onClick={() => setDeletingVersionId(bv.id)}
                            className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-opacity"
                            title="Delete this BOM version"
                          >×</button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 pt-3 border-t border-gray-200">
            <Button loading={saving} onClick={handleSave}>Save</Button>
            <Button variant="ghost" onClick={closePanel}>Cancel</Button>
            {selected && !selected.deleted_at && (
              <Button variant="danger" size="sm" className="ml-auto" onClick={handleDelete}>Delete</Button>
            )}
            {selected?.deleted_at && (
              <Button variant="secondary" size="sm" className="ml-auto" onClick={handleRestore}>Restore</Button>
            )}
          </div>

          {selected && <PanelDocuments entityType="parent_item" entityId={selected.id} />}
        </div>
      </SlideOutPanel>

      <ImportBomModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        importForm={importForm}
        setImportForm={setImportForm}
        importFile={importFile}
        setImportFile={setImportFile}
        fileInputRef={fileInputRef}
        importing={importing}
        importReport={importReport}
        onImport={handleImport}
        onDownloadTemplate={handleDownloadTemplate}
        lockedToItem={!!selected}
      />
    </div>
  );
}

function ImportBomModal({ open, onClose, importForm, setImportForm, importFile, setImportFile, fileInputRef, importing, importReport, onImport, onDownloadTemplate, lockedToItem }) {
  if (!open) return null;
  const hasErrors = importReport?.errors?.length > 0;
  const hasWarnings = importReport?.warnings?.length > 0;
  const success = importReport && !hasErrors;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-xl mx-4 max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Import BOM</h2>
            <p className="text-xs text-gray-500 mt-0.5">CSV or Excel — PartNumber, Quantity, Description, User Name, Cost, HSH PT#, Supplier, url</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1 space-y-4">

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">File <span className="text-red-500">*</span></label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
              >
                {importFile ? 'Change file' : 'Choose file…'}
              </button>
              {importFile && (
                <span className="text-sm text-gray-600 truncate max-w-[220px]">{importFile.name}</span>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                className="hidden"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
              />
            </div>
            <button
              type="button"
              onClick={onDownloadTemplate}
              className="mt-1 text-xs text-blue-600 hover:underline"
            >
              Download template CSV
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Parent Item Number <span className="text-red-500">*</span></label>
            {lockedToItem ? (
              <div className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-gray-50 text-gray-700 font-mono">
                {importForm.parent_item_no}
              </div>
            ) : (
              <>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  placeholder="e.g. PS-4X3-100"
                  value={importForm.parent_item_no}
                  onChange={(e) => setImportForm({ ...importForm, parent_item_no: e.target.value })}
                />
                <p className="text-xs text-gray-400 mt-0.5">If this exists, a new BOM version is created. If not, a new parent item is created.</p>
              </>
            )}
          </div>

          {!lockedToItem && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Parent Description <span className="text-gray-400 font-normal">(required for new parent items)</span></label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  placeholder="e.g. 4x3 Centrifugal Pump System 100GPM"
                  value={importForm.parent_description}
                  onChange={(e) => setImportForm({ ...importForm, parent_description: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Est. Labor Hours</label>
                  <input
                    type="number"
                    step="0.5"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    placeholder="e.g. 80"
                    value={importForm.estimated_labor_hours ?? ''}
                    onChange={(e) => setImportForm({ ...importForm, estimated_labor_hours: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">List Price ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    placeholder="e.g. 5000"
                    value={importForm.list_price ?? ''}
                    onChange={(e) => setImportForm({ ...importForm, list_price: e.target.value })}
                  />
                </div>
              </div>
            </>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Version Label <span className="text-gray-400 font-normal">(optional)</span></label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder='e.g. "Rev B"'
                value={importForm.version_label}
                onChange={(e) => setImportForm({ ...importForm, version_label: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Version Notes <span className="text-gray-400 font-normal">(optional)</span></label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder="What changed?"
                value={importForm.version_notes}
                onChange={(e) => setImportForm({ ...importForm, version_notes: e.target.value })}
              />
            </div>
          </div>

          {importReport && (
            <div className={`rounded-md border p-3 text-sm space-y-2 ${
              hasErrors ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'
            }`}>
              {success && (
                <div className="font-medium text-green-800">
                  ✓ Import complete — {importReport.lines_created} BOM line{importReport.lines_created !== 1 ? 's' : ''} created
                  {importReport.mode === 'new' ? ' (new parent item)' : ` (v${importReport.version_number})`}
                </div>
              )}
              {hasErrors && <div className="font-medium text-red-800">✗ Import failed</div>}
              {success && (
                <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-gray-600">
                  <span>Parts created: <strong>{importReport.parts_created}</strong></span>
                  <span>Parts matched: <strong>{importReport.parts_existing}</strong></span>
                  <span>Vendors created: <strong>{importReport.vendors_created}</strong></span>
                  <span>Vendors matched: <strong>{importReport.vendors_existing}</strong></span>
                  <span>Vendor parts created: <strong>{importReport.vendor_parts_created}</strong></span>
                  <span>Vendor parts updated: <strong>{importReport.vendor_parts_updated}</strong></span>
                  {importReport.duplicates_merged > 0 && (
                    <span className="col-span-2">Duplicates merged: <strong>{importReport.duplicates_merged}</strong></span>
                  )}
                  {importReport.preferred_vendor_changed > 0 && (
                    <span className="col-span-2">Preferred vendor updated: <strong>{importReport.preferred_vendor_changed}</strong></span>
                  )}
                </div>
              )}
              {importReport.needs_review_vendors?.length > 0 && (
                <div className="text-xs">
                  <span className="font-medium text-amber-700">⚠ New vendors need contact info added: </span>
                  <span className="text-amber-700">{importReport.needs_review_vendors.join(', ')}</span>
                </div>
              )}
              {importReport.needs_review_parts?.length > 0 && (
                <div className="text-xs">
                  <span className="font-medium text-amber-700">⚠ Parts flagged for review: </span>
                  <span className="text-amber-700">{importReport.needs_review_parts.join(', ')}</span>
                </div>
              )}
              {hasWarnings && (
                <div className="text-xs space-y-0.5">
                  <div className="font-medium text-amber-700">Warnings:</div>
                  {importReport.warnings.map((w, i) => <div key={i} className="text-amber-700">— {w}</div>)}
                </div>
              )}
              {hasErrors && (
                <div className="text-xs space-y-0.5">
                  {importReport.errors.map((e, i) => <div key={i} className="text-red-700">— {e}</div>)}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-gray-200 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={importing}>
            {success ? 'Close' : 'Cancel'}
          </Button>
          {!success && (
            <Button onClick={onImport} loading={importing}>Import</Button>
          )}
        </div>
      </div>
    </div>
  );
}


function TextInput({ label, value, onChange, type = 'text', multiline = false }) {
  const cls = "w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500";
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {multiline ? (
        <textarea className={cls} rows={2} value={value || ''} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input type={type} className={cls} value={value || ''} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}
